import { createI18n } from "vue-i18n";

const loadedLanguages = [];

const dateTimeFormats = {
  en: {
    "date-short": {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    },
    "date-long": {
      year: "numeric",
      month: "long",
      day: "numeric"
    },
    "date-time": {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }
  },
  da: {
    "date-short": {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    },
    "date-long": {
      day: "numeric",
      month: "long",
      year: "numeric"
    },
    "date-time": {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    }
  },
  de: {
    "date-short": {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    },
    "date-long": {
      day: "numeric",
      month: "long",
      year: "numeric"
    }
  }
};

/**
 * Export i18n instance
 */
export const i18n = createI18n({
  legacy: false,
  globalInjection: true, // exposes $t, $tc, $te, $d, $n in component templates
  // Suppress "key not found" warnings — locale messages are loaded asynchronously after
  // the first render, so missing-key warnings would otherwise fire transiently for every
  // template binding before the language file resolves.
  missingWarn: false,
  fallbackWarn: false,
  // Locale strings are static JSON (no user input), so HTML inside messages is intentional
  // and rendered via v-html in tooltips/help text. Suppress the XSS recommendation warning.
  warnHtmlMessage: true,
  locale: import.meta.env.VITE_APP_I18N_LOCALE || "da",
  fallbackLocale: import.meta.env.VITE_APP_I18N_FALLBACK_LOCALE || "da",
  messages: {}, // set locale messages
  datetimeFormats: dateTimeFormats
});

/**
 * Sets the language for the website
 * @param { string } lang Locale language code
 * @returns { string } Local language code
 */
function setI18nLanguage(lang) {
  // In Composition Mode (legacy: false), `locale` is a Ref that must be assigned via .value
  i18n.global.locale.value = lang;
  document.querySelector("html").setAttribute("lang", lang);
  return lang;
}

/**
 * Loads the language asynchronously
 * @param { string } language Locale language code
 * @param { string } languageVariant Variant of language - eg skole, dagtilbud, nwp
 * @param { string } customerFile Customer specific file name
 * @returns { Promise } Promise
 */
export async function loadLanguageAsync(language, languageVariant, customerFile) {
  // If the same language
  if (i18n.global.locale.value === language) {
    setI18nLanguage(language);
  }

  // If the language was already loaded
  if (loadedLanguages.includes(language)) {
    return Promise.resolve(setI18nLanguage(language));
  }

  // If the language hasn't been loaded yet
  return import(/* webpackChunkName: "lang-[request]" */ `../../locales/${language}-${languageVariant}.json`).then(
    translations => {
      let translationData = translations.default;
      if (customerFile) {
        import(
          /* webpackChunkName: "lang-[request]" */ `../../locales/customerspecific/${language}-${customerFile}.json`
        )
          .then(customerSpecificTranslations => {
            translationData = {
              ...translations.default,
              ...customerSpecificTranslations.default // Merge translations
            };

            i18n.global.setLocaleMessage(language, translationData);
            loadedLanguages.push(language);
            return setI18nLanguage(language);
          })
          .catch(() => {
            i18n.global.setLocaleMessage(language, translationData);
            loadedLanguages.push(language);
            return setI18nLanguage(language);
          });
      } else {
        i18n.global.setLocaleMessage(language, translationData);
        loadedLanguages.push(language);
        return setI18nLanguage(language);
      }
    }
  );
}
